// Background sync service for offline data persistence

import { offlineStorage } from './offlineStorage';
import { registerBackgroundSync } from './serviceWorkerRegistration';
import { QuestionData } from '../../types/questionData';
import { apiClient } from '../api/client';

class BackgroundSyncService {
  private syncInProgress = false;
  private syncInterval: number | null = null;

  // Initialize background sync
  async init() {
    try {
      // Register for background sync (only in production)
      if (!import.meta.env.DEV) {
        await registerBackgroundSync('sync-whiteboard-data');
      }

      // Listen for online/offline events
      window.addEventListener('online', () => {
        console.log('App is online - starting sync');
        this.syncPendingData();
      });

      window.addEventListener('offline', () => {
        console.log('App is offline - data will be synced when online');
      });

      // Start periodic sync if online
      if (navigator.onLine) {
        this.startPeriodicSync();
      }
    } catch (error) {
      console.warn('Background sync initialization failed:', error);
    }
  }

  // Add data to sync queue
  async queueForSync(
    type: 'questionData' | 'testSession' | 'answer',
    url: string,
    method: string,
    headers: Record<string, string>,
    body: any
  ) {
    await offlineStorage.addToSyncQueue({
      timestamp: Date.now(),
      type,
      url,
      method,
      headers,
      body: JSON.stringify(body),
      retryCount: 0
    });

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.syncPendingData();
    }
  }

  // Sync all pending data
  async syncPendingData() {
    if (this.syncInProgress || !navigator.onLine) {
      return;
    }

    this.syncInProgress = true;

    try {
      const syncQueue = await offlineStorage.getSyncQueue();
      console.log(`Syncing ${syncQueue.length} pending items`);

      for (const item of syncQueue) {
        try {
          // Make the API request
          const response = await fetch(item.url, {
            method: item.method,
            headers: item.headers,
            body: item.body
          });

          if (response.ok) {
            // Remove from sync queue on success
            await offlineStorage.removeSyncQueueItem(item.id);
            console.log(`Successfully synced item ${item.id}`);
          } else {
            // Increment retry count
            if (item.retryCount < 3) {
              item.retryCount++;
              await offlineStorage.addToSyncQueue(item);
            } else {
              console.error(`Failed to sync item ${item.id} after 3 retries`);
              await offlineStorage.removeSyncQueueItem(item.id);
            }
          }
        } catch (error) {
          console.error(`Error syncing item ${item.id}:`, error);
          
          // Increment retry count
          if (item.retryCount < 3) {
            item.retryCount++;
            await offlineStorage.addToSyncQueue(item);
          } else {
            await offlineStorage.removeSyncQueueItem(item.id);
          }
        }
      }
    } catch (error) {
      console.error('Error during sync:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Queue question data for sync
  async queueQuestionData(
    tenantId: number,
    userId: number,
    testId: string,
    questionId: string,
    data: Partial<QuestionData>
  ) {
    // Save to IndexedDB first
    await offlineStorage.saveQuestionData({
      ...data,
      userId,
      tenantId,
      testId,
      questionId,
      updatedAt: Date.now()
    } as QuestionData);

    // Queue for sync
    const url = `/api/services/app/QuestionData/SaveQuestionData`;
    const headers = {
      'Content-Type': 'application/json',
      'Abp.TenantId': tenantId.toString()
    };
    const body = {
      userId,
      testId,
      questionId,
      data
    };

    await this.queueForSync('questionData', url, 'POST', headers, body);
  }

  // Queue test session for sync
  async queueTestSession(session: any) {
    const url = `/api/services/app/TestSession/SaveSession`;
    const headers = {
      'Content-Type': 'application/json'
    };

    await this.queueForSync('testSession', url, 'POST', headers, session);
  }

  // Start periodic sync (every 5 minutes)
  startPeriodicSync() {
    if (this.syncInterval) {
      return;
    }

    this.syncInterval = window.setInterval(() => {
      if (navigator.onLine) {
        this.syncPendingData();
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Stop periodic sync
  stopPeriodicSync() {
    if (this.syncInterval) {
      window.clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // Get sync status
  async getSyncStatus(): Promise<{
    pendingCount: number;
    lastSyncTime?: number;
    isOnline: boolean;
  }> {
    try {
      const syncQueue = await offlineStorage.getSyncQueue();
      const lastSync = await offlineStorage.getPreference('lastSyncTime');

      return {
        pendingCount: syncQueue.length,
        lastSyncTime: lastSync,
        isOnline: navigator.onLine
      };
    } catch (error) {
      // Return default values if storage isn't available
      console.warn('Could not get sync status:', error);
      return {
        pendingCount: 0,
        lastSyncTime: undefined,
        isOnline: navigator.onLine
      };
    }
  }

  // Force sync now
  async forceSync() {
    if (navigator.onLine) {
      await this.syncPendingData();
      await offlineStorage.savePreference('lastSyncTime', Date.now());
    } else {
      throw new Error('Cannot sync while offline');
    }
  }

  // Clear sync queue
  async clearSyncQueue() {
    const syncQueue = await offlineStorage.getSyncQueue();
    for (const item of syncQueue) {
      await offlineStorage.removeSyncQueueItem(item.id);
    }
  }
}

// Export singleton instance
export const backgroundSync = new BackgroundSyncService();